import React, { useState, useEffect, CSSProperties } from 'react';
import { QuestionFolder } from '@/types';

interface CreateFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (folderData: { name: string; description?: string; icon?: string; color?: string; parentFolderId?: string }) => Promise<void>;
  parentFolderId?: string | null;
  folderToEdit?: QuestionFolder | null; // If provided, we're editing instead of creating
}

const FOLDER_ICONS = ['📁', '📂', '📚', '📝', '🎓', '🎯', '🌟', '🔬', '🎨', '💡', '🏆', '🎮'];
const FOLDER_COLORS = [
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#10B981', // Green
  '#F59E0B', // Amber
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#F97316', // Orange
];

export const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
  isOpen,
  onClose,
  onCreate,
  parentFolderId,
  folderToEdit
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedIcon, setSelectedIcon] = useState(FOLDER_ICONS[0]);
  const [selectedColor, setSelectedColor] = useState(FOLDER_COLORS[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form when editing
  useEffect(() => {
    if (folderToEdit) {
      setName(folderToEdit.name);
      setDescription(folderToEdit.description || '');
      setSelectedIcon(folderToEdit.icon || FOLDER_ICONS[0]);
      setSelectedColor(folderToEdit.color || FOLDER_COLORS[0]);
    } else {
      // Reset form for new folder
      setName('');
      setDescription('');
      setSelectedIcon(FOLDER_ICONS[0]);
      setSelectedColor(FOLDER_COLORS[0]);
    }
    setError(null);
  }, [folderToEdit, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Folder name is required');
      return;
    }

    try {
      setIsSubmitting(true);
      await onCreate({
        name: name.trim(),
        description: description.trim() || undefined,
        icon: selectedIcon,
        color: selectedColor,
        parentFolderId: parentFolderId || undefined
      });
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create folder');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Styles
  const overlayStyle: CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
    animation: 'fadeIn 0.2s ease'
  };

  const modalStyle: CSSProperties = {
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%)',
    borderRadius: '24px',
    padding: '2rem',
    maxWidth: '560px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    border: '2px solid rgba(102, 126, 234, 0.2)',
    animation: 'slideIn 0.3s ease',
    position: 'relative',
    maxHeight: '90vh',
    overflowY: 'auto'
  };

  const headerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1.5rem'
  };

  const iconWrapperStyle: CSSProperties = {
    fontSize: '2.5rem',
    background: `linear-gradient(135deg, ${selectedColor} 0%, ${selectedColor}dd 100%)`,
    borderRadius: '16px',
    width: '64px',
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 4px 15px ${selectedColor}40`,
    transition: 'all 0.3s ease'
  };

  const titleStyle: CSSProperties = {
    margin: 0,
    fontSize: '1.75rem',
    fontWeight: 'bold',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent'
  };

  const labelStyle: CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '0.5rem'
  };

  const inputStyle: CSSProperties = {
    width: '100%',
    padding: '0.875rem 1rem',
    fontSize: '1rem',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    outline: 'none',
    transition: 'all 0.2s ease',
    backgroundColor: 'white'
  };

  const textareaStyle: CSSProperties = {
    ...inputStyle,
    resize: 'none',
    fontFamily: 'inherit',
    minHeight: '80px'
  };

  const sectionStyle: CSSProperties = {
    marginBottom: '1.5rem'
  };

  const iconButtonBaseStyle: CSSProperties = {
    padding: '0.75rem',
    fontSize: '1.75rem',
    borderRadius: '12px',
    border: '2px solid #e5e7eb',
    background: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const colorButtonBaseStyle: CSSProperties = {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    border: '3px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.25rem',
    fontWeight: 'bold'
  };

  const primaryButtonStyle: CSSProperties = {
    padding: '0.875rem 1.75rem',
    background: `linear-gradient(135deg, ${selectedColor} 0%, ${selectedColor}dd 100%)`,
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: isSubmitting || !name.trim() ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: `0 4px 15px ${selectedColor}40`,
    opacity: isSubmitting || !name.trim() ? 0.6 : 1,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    justifyContent: 'center'
  };

  const cancelButtonStyle: CSSProperties = {
    padding: '0.875rem 1.75rem',
    background: 'white',
    color: '#64748b',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    opacity: isSubmitting ? 0.6 : 1
  };

  return (
    <>
      <div style={overlayStyle} onClick={onClose}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div style={headerStyle}>
            <div style={iconWrapperStyle}>
              {selectedIcon}
            </div>
            <h2 style={titleStyle}>
              {folderToEdit ? 'Edit Folder' : 'Create New Folder'}
            </h2>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Folder Name */}
            <div style={sectionStyle}>
              <label style={labelStyle}>
                Folder Name <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Mathematics, Science, Grade 5"
                style={inputStyle}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = selectedColor;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${selectedColor}20`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                maxLength={50}
                autoFocus
              />
            </div>

            {/* Description */}
            <div style={sectionStyle}>
              <label style={labelStyle}>
                Description <span style={{ color: '#94a3b8', fontWeight: 'normal' }}>(Optional)</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add a description for this folder..."
                style={textareaStyle}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = selectedColor;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${selectedColor}20`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                rows={3}
                maxLength={200}
              />
            </div>

            {/* Icon Selection */}
            <div style={sectionStyle}>
              <label style={labelStyle}>Choose Icon</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.5rem' }}>
                {FOLDER_ICONS.map((icon) => {
                  const isSelected = selectedIcon === icon;
                  return (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setSelectedIcon(icon)}
                      style={{
                        ...iconButtonBaseStyle,
                        borderColor: isSelected ? selectedColor : '#e5e7eb',
                        backgroundColor: isSelected ? `${selectedColor}15` : 'white',
                        transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                        boxShadow: isSelected ? `0 4px 12px ${selectedColor}30` : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = '#cbd5e1';
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.transform = 'scale(1)';
                        }
                      }}
                    >
                      {icon}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Color Selection */}
            <div style={sectionStyle}>
              <label style={labelStyle}>Choose Color</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' }}>
                {FOLDER_COLORS.map((color) => {
                  const isSelected = selectedColor === color;
                  return (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setSelectedColor(color)}
                      style={{
                        ...colorButtonBaseStyle,
                        backgroundColor: `${color}25`,
                        borderColor: isSelected ? color : 'transparent',
                        transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                        boxShadow: isSelected ? `0 4px 15px ${color}40` : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.transform = 'scale(1.05)';
                          e.currentTarget.style.backgroundColor = `${color}35`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.backgroundColor = `${color}25`;
                        }
                      }}
                    >
                      {isSelected && '✓'}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div style={{
                padding: '1rem',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '2px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '12px',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                color: '#dc2626',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}>
                <span style={{ fontSize: '1.25rem' }}>⚠️</span>
                {error}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem' }}>
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                style={cancelButtonStyle}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.color = '#667eea';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.color = '#64748b';
                  }
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !name.trim()}
                style={primaryButtonStyle}
                onMouseEnter={(e) => {
                  if (!isSubmitting && name.trim()) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = `0 6px 20px ${selectedColor}50`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting && name.trim()) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = `0 4px 15px ${selectedColor}40`;
                  }
                }}
              >
                {isSubmitting ? (
                  <>
                    <span style={{ animation: 'spin 1s linear infinite' }}>⏳</span>
                    {folderToEdit ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <span>{folderToEdit ? '✏️' : '✨'}</span>
                    {folderToEdit ? 'Update Folder' : 'Create Folder'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};
